import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import ICalendarTimeBlock from '../../_interfaces/ICalendarTimeBlock';
import ISite from '../../_interfaces/ISite';
import { environment } from '../../../environments/environment';

export interface CreateCalendarTimeBlocksBulkPayload {
  calendars_ids: number[];
  start: string | Date;
  end: string | Date;
  reason?: string | null;
  notes?: string | null;
}

export interface UpdateCalendarTimeBlockPayload {
  calendars_id?: number;
  start?: string | Date;
  end?: string | Date;
  reason?: string | null;
  notes?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarTimeBlocksService {
  constructor(private readonly http: HttpClient) {}

  listBySite(
    customers_id: number,
    site: ISite,
    options?: { calendars_ids?: number[]; start?: string; end?: string },
  ) {
    const params: Record<string, string> = {};
    if (options?.calendars_ids?.length) {
      params['calendars_ids'] = options.calendars_ids.join(',');
    }
    if (options?.start) {
      params['start'] = this.normalizeDateParam(options.start);
    }
    if (options?.end) {
      params['end'] = this.normalizeDateParam(options.end);
    }

    return this.http.get<ICalendarTimeBlock[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/calendar-time-blocks`,
      { params },
    );
  }

  createBulk(
    customers_id: number,
    site: ISite,
    payload: CreateCalendarTimeBlocksBulkPayload,
  ) {
    return this.http.post<ICalendarTimeBlock[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/calendar-time-blocks/bulk`,
      {
        calendars_ids: payload.calendars_ids,
        start: this.normalizePayloadDate(payload.start),
        end: this.normalizePayloadDate(payload.end),
        reason: payload.reason || undefined,
        notes: payload.notes || undefined,
      },
    );
  }

  update(
    customers_id: number,
    site: ISite,
    time_blocks_id: number,
    payload: UpdateCalendarTimeBlockPayload,
  ) {
    return this.http.patch<ICalendarTimeBlock>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/calendar-time-blocks/${time_blocks_id}`,
      {
        calendars_id: payload.calendars_id,
        start: payload.start ? this.normalizePayloadDate(payload.start) : undefined,
        end: payload.end ? this.normalizePayloadDate(payload.end) : undefined,
        reason: payload.reason ?? null,
        notes: payload.notes ?? null,
      },
    );
  }

  remove(customers_id: number, site: ISite, time_blocks_id: number) {
    return this.http.delete(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/calendar-time-blocks/${time_blocks_id}`,
    );
  }

  private normalizeDateParam(value: string): string {
    const parsed = moment(value, 'DD-MM-YYYY HH:mm:ss', true);
    return parsed.isValid() ? parsed.toISOString() : value;
  }

  private normalizePayloadDate(value: string | Date): string {
    return moment(value).toISOString();
  }
}